import React, { useId } from 'react'

/**
 * Die **Tropfenform** — auf der Pinnwand, im Icon-Wähler und im
 * Titelwerkzeug. Ursprünglich die eines Canvas-Elements: der Umriss, in dem sowohl der
 * Farb-Tropfen der **Options-Leiste** als auch jedes Feld des **Farbwählers**
 * seine Farbe zeigt.
 *
 * Der Pfad ist aus dem Figma GEZOGEN (Node `5083:52029`), nicht nachgebaut: Es
 * ist ein um 45° gedrehtes Quadrat mit drei runden Ecken und einer spitzen —
 * in CSS ginge das nur über eine zweite, gedrehte Box, und die ließe sich nicht
 * mit zwei Farben füllen.
 *
 * **Zwei Farben heißt uneinheitlich.** Bei einer Mehrfachauswahl mit
 * verschiedenen Farben wird der Tropfen diagonal geteilt (so macht es FigJam),
 * statt eine Farbe zu behaupten, die nicht für alle gilt.
 *
 * Eine Kontur bringt er von sich aus nicht mit: Beide Aufrufer stehen auf der
 * dunklen Fläche der Leiste, wo auch der weiße Tropfen für sich steht. Der
 * Rand des Farb-Chips in der Leiste kommt aus der SCSS (`&__chip`), weil er
 * zur Farbfamilie gehört und nicht zur Form.
 *
 * **Die GEWÄHLTE Farbe im Farbwähler trägt einen Ring — in Tropfenform.**
 * Ein runder Ring um den Knopf passte nicht zur Form; also zeichnet der
 * Tropfen ihn selbst, wie `ring` in CSS: innen der Farbtropfen, dann
 * TRANSPARENTE Luft in Tropfenform, dann die Kontur. Der Ring ist ein
 * breiter Strich in `outline` hinter der Füllung; eine Maske stanzt seinen
 * inneren Teil aus (Umriss plus Luft), sodass der Grund durchscheint — auch
 * ein Hover hinter dem Tropfen.
 *
 * @example
 * <ColorDrop fill="#FFF7A3" />
 * <ColorDrop fill={['#FFF7A3', '#D9CCFF']} />
 * <ColorDrop fill="#FFF7A3" outline="var(--bar-chip-border)" />
 */
export interface ColorDropProps {
  /** Eine Farbe — oder zwei, dann wird der Tropfen geteilt. */
  fill: string | readonly [string, string]
  /** Höhe in px; die Breite folgt dem Seitenverhältnis. */
  size?: number
  /** Ring in Tropfenform um die Füllung, mit Luft dazwischen — die Markierung der gewählten Farbe. */
  outline?: string
  className?: string
}

/*
  Maße des Rings in ViewBox-Einheiten (bei 18px Höhe ≈ 1px je Einheit),
  jeweils ab dem Umriss der Füllung nach außen: erst die Luft, dann die
  Kontur. Striche liegen mittig auf dem Pfad, deshalb das Doppelte.
*/
const RING_GAP = 1.5
const RING_WIDTH = 1.5
const CUTOUT_STROKE = 2 * RING_GAP
const RING_STROKE = 2 * (RING_GAP + RING_WIDTH)

/** Der Umriss aus dem Figma, in seinem eigenen Koordinatenraum. */
const DROP_PATH = 'M6.74292 0.520699C7.43719 -0.173566 8.56281 -0.173566 9.25708 0.520699L13.6569 4.92047C16.781 8.04467 16.781 13.11 13.6569 16.2342C10.5327 19.3584 5.46734 19.3584 2.34315 16.2342C-0.781049 13.11 -0.781049 8.04467 2.34315 4.92047L6.74292 0.520699Z'
const VIEW_BOX = '-0.79 -0.18 17.57 18.76'
const ASPECT = 17.57 / 18.76

export const ColorDrop: React.FC<ColorDropProps> = ({ fill, size = 16, outline, className }) => {
  // Eigene Kennung je Instanz: Zwei Tropfen auf derselben Seite dürfen sich
  // ihren Verlauf nicht teilen, sonst zeigt der zweite die Farben des ersten.
  const gradientId = useId()
  const maskId = useId()
  const split = Array.isArray(fill)

  return (
    <svg
      className={className}
      width={Math.round(size * ASPECT)}
      height={size}
      viewBox={VIEW_BOX}
      fill="none"
      aria-hidden="true"
      focusable="false"
      // Die Kontur steht über den Rand der ViewBox hinaus.
      overflow="visible"
    >
      {outline && (
        <>
          <defs>
            {/*
              Weiß lässt durch, Schwarz stanzt aus: der Tropfen samt Luft.
              `maskUnits="userSpaceOnUse"` mit großzügigem Rechteck, damit
              auch der Überhang des Rings außerhalb der ViewBox erhalten bleibt.
            */}
            <mask id={maskId} maskUnits="userSpaceOnUse" x="-10" y="-10" width="40" height="40">
              <rect x="-10" y="-10" width="40" height="40" fill="white" />
              <path d={DROP_PATH} fill="black" stroke="black" strokeWidth={CUTOUT_STROKE} strokeLinejoin="round" />
            </mask>
          </defs>
          <path
            className="canvas-note-drop__ring"
            d={DROP_PATH}
            fill="none"
            stroke={outline}
            strokeWidth={RING_STROKE}
            strokeLinejoin="round"
            mask={`url(#${maskId})`}
          />
        </>
      )}
      {split && (
        <defs>
          {/*
            Harte Kanten (0/50/50/100) statt eines weichen Verlaufs: Es sind
            zwei Farben und kein Übergang. Die Achse läuft von oben links nach
            unten rechts, damit die Teilung auch im 16px-Tropfen zu sehen ist.
          */}
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="50%" stopColor={(fill as readonly [string, string])[0]} />
            <stop offset="50%" stopColor={(fill as readonly [string, string])[1]} />
          </linearGradient>
        </defs>
      )}
      {/*
        Die Klassen sind die Adresse für die SCSS des Farbwählers: Dessen
        Rand-Regel gilt NUR der Füllung — träfe sie jeden Pfad, überschriebe
        sie auch den Ring (Strichfarbe und -breite) und stanzte die Maske um.
      */}
      <path
        className="canvas-note-drop__fill"
        d={DROP_PATH}
        fill={split ? `url(#${gradientId})` : (fill as string)}
      />
    </svg>
  )
}
